import React from 'react'
import { useSelector } from 'react-redux'
import { type Constant, Utils } from '@pbt/pbt-ui-components'

import EnumSelect, {
  type EnumSelectProps,
} from '~/components/common/inputs/EnumSelect'
import RhapStatusSelect from '~/components/common/RhapStatusSelect'
import { ImagingOrderStatus, ImagingStatusColorMap } from '~/constants/imaging'
import { getImagingOrderStatuses } from '~/store/reducers/constants'

import { isCompletedImagingStatus } from './imagingProceduresUtils'

const isAppropriateStatus =
  (canCancel: boolean, hasResult: boolean) =>
  ({ name }: Constant) => {
    switch (name) {
      case ImagingOrderStatus.CANCELLED:
        return canCancel
      case ImagingOrderStatus.IN_PROGRESS:
      case ImagingOrderStatus.ORDERED:
      case ImagingOrderStatus.PENDING:
        return !hasResult
      default:
        return true
    }
  }

const filterImagingStatusOptions = (
  ImagingOrderStatuses: Constant[],
  isSoapFinalized: boolean,
  actualStatus: ImagingOrderStatus,
) => {
  const hasResult = isCompletedImagingStatus(actualStatus)
  const canCancel = !isSoapFinalized && !hasResult

  return ImagingOrderStatuses.filter(isAppropriateStatus(canCancel, hasResult))
}

export interface ImagingStatusSelectProps
  extends Omit<EnumSelectProps, 'accent' | 'Constant'> {
  isChirp?: boolean
  isSoapFinalized: boolean
}

const ImagingStatusSelect = ({
  field,
  isSoapFinalized,
  isChirp = false,
  ...rest
}: ImagingStatusSelectProps) => {
  const ImagingOrderStatuses = useSelector(getImagingOrderStatuses)
  const initialStatusName = Utils.getConstantName(
    field.initialValue,
    ImagingOrderStatuses,
  )
  const options = filterImagingStatusOptions(
    ImagingOrderStatuses,
    isSoapFinalized,
    initialStatusName,
  )

  if (isChirp) {
    return (
      <RhapStatusSelect
        colorMap={ImagingStatusColorMap}
        getOptionId={(option) => option.id}
        getOptionName={(option) => option.name}
        label={field.label ?? ''}
        options={options}
        value={field.value}
        onChange={(newValue) => field.setValue(newValue)}
      />
    )
  }

  return <EnumSelect accent Constant={options} field={field} {...rest} />
}

export default ImagingStatusSelect
